import React, { useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
    PastPeriodKey,
    useDashboard,
} from '../../../../contexts/DashboardContext';
import LoadingPlaceholder from '../../../../muiComponents/LoadingPlaceholder';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import useTranslate from '../../../../customHooks/useTranslate';
import { MenuItem, Select } from '@mui/material';
import {
    BookingStatusColor,
    formatPastPeriodSubtitle,
    getData,
} from '../../../../utils/dashboard/dashbordUtils';
import useLanguage from '../../../../customHooks/useLanguage';
import { capitalize } from '../../../../utils/usefulFunctions';
import { getCSSVariable } from '../../../../stylesheets/cssVariablesUtils';
import CheckboxInput from '../../../../muiComponents/CheckboxInput';

// Registrar componentes de Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const BookingHistoryChart = () => {
    const { bookingsHistory, changeBookingsHistoryPeriod } = useDashboard();
    const translate = useTranslate();
    const language = useLanguage();
    const chartTitle = translate('history_bookings');

    const [isCancelledVisible, setIsCancelledVisible] = useState(true);
    const [isCommittedVisible, setIsCommittedVisible] = useState(true);

    const options: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    title: tooltipItems => {
                        const index = tooltipItems[0].dataIndex;
                        return data[index].longLabel;
                    },
                    label: tooltipItem => {
                        return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
                    },
                },
            },
        },
        scales: {
            x: {
                grid: {
                    drawOnChartArea: false,
                    drawTicks: false,
                },
                ticks: {
                    // beginAtZero: true,
                    font: {
                        size: 8,
                        family: getCSSVariable('mainFont'),
                    },
                    padding: 5,
                },
            },
            y: {
                grid: {
                    lineWidth: 0.5,
                    drawTicks: false,
                },
                ticks: {
                    // beginAtZero: true,
                    font: {
                        size: 8,
                        family: getCSSVariable('mainFont'),
                    },
                    padding: 5,
                    callback: label => {
                        if (typeof label === 'number') {
                            return Math.floor(label) === label ? label : null;
                        }
                        return label;
                    },
                },
            },
        },
    };

    const periodSubtitle = useMemo(() => {
        const subtitle = formatPastPeriodSubtitle(
            bookingsHistory.period.key,
            language,
            translate
        );
        return capitalize(subtitle);
    }, [bookingsHistory.period.key, language, translate]);

    const data = useMemo(
        () =>
            getData(bookingsHistory.data, bookingsHistory.period.key, language),
        [bookingsHistory.data, bookingsHistory.period.key, language]
    );

    return (
        <>
            <div>
                <div className='flex flex-align-center flex-between'>
                    <div className='flex-column gap-5'>
                        <SectionTitle title={chartTitle} variant='h3' />
                        <p className='landing-chart__subtitle'>
                            {periodSubtitle}
                        </p>
                    </div>
                    <Select
                        size='small'
                        className='landing-chart__period-select'
                        value={bookingsHistory.period.key}
                        onChange={e =>
                            changeBookingsHistoryPeriod(
                                e.target.value as PastPeriodKey
                            )
                        }
                    >
                        {Object.entries(PastPeriodKey).map(([key, value]) => (
                            <MenuItem key={key} value={value}>
                                {translate(value)}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
                {bookingsHistory.isDataLoaded && bookingsHistory.data.length ? (
                    <div className='flex flex-end gap-20'>
                        <CheckboxInput
                            label={translate('committed')}
                            checked={isCommittedVisible}
                            sx={{
                                '&.Mui-checked': {
                                    color: BookingStatusColor.COMMITTED,
                                },
                            }}
                            onChange={() =>
                                setIsCommittedVisible(prev => !prev)
                            }
                            disabled={!isCancelledVisible}
                        />
                        <CheckboxInput
                            label={translate('cancelled')}
                            checked={isCancelledVisible}
                            sx={{
                                '&.Mui-checked': {
                                    color: BookingStatusColor.CANCELLED,
                                },
                            }}
                            onChange={() =>
                                setIsCancelledVisible(prev => !prev)
                            }
                            disabled={!isCommittedVisible}
                        />
                    </div>
                ) : (
                    ''
                )}
            </div>

            {bookingsHistory.isLoading ? (
                <LoadingPlaceholder
                    variant='rounded'
                    width={'100%'}
                    height={'100%'}
                />
            ) : bookingsHistory.isDataLoaded && bookingsHistory.data.length ? (
                <div className='flex-grow flex-center-center'>
                    <Bar
                        data={{
                            labels: data.map(item => item.label),
                            datasets: [
                                ...(isCommittedVisible
                                    ? [
                                          {
                                              label: translate('committed'),
                                              data: data.map(
                                                  item => item.committedCount
                                              ),
                                              backgroundColor:
                                                  BookingStatusColor.COMMITTED,
                                          },
                                      ]
                                    : []),
                                ...(isCancelledVisible
                                    ? [
                                          {
                                              label: translate('cancelled'),
                                              data: data.map(
                                                  item => item.cancelledCount
                                              ),
                                              backgroundColor:
                                                  BookingStatusColor.CANCELLED,
                                          },
                                      ]
                                    : []),
                            ],
                        }}
                        options={options}
                    />
                </div>
            ) : (
                <div className='flex-grow flex-center-center text-size-16 font-weight-500'>
                    {translate('no_data_for_period')}
                </div>
            )}
        </>
    );
};

export default BookingHistoryChart;
